import { useEffect, useCallback, useState } from 'react';
import useApi from './useApi';
import { ChangeSetBM, ChangeSetPagedResult } from '../types/ChangeSetTypes';
import { useAppSettings } from '../hooks/useAppSettings';
import { mapChangeSetBMs } from '../factory/ChangeSetFactory';

interface GetChangeSetsParams {
    skip?: number;
    take?: number;
    sortBy?: string;
    sortOrder?: string;
    year?: number;
}

export default () => {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [totalCount, setTotalCount] = useState<number>(0);

    const getChangeSets = useCallback(
        async (params: GetChangeSetsParams = {}) => {
            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const { skip = 0, take = 100, sortBy = 'DateTimeUtc', sortOrder = 'desc', year } = params;
                const queryParams = new URLSearchParams({
                    skip: skip.toString(),
                    take: take.toString(),
                    sortBy,
                    sortOrder,
                });

                if (year) {
                    queryParams.append('year', year.toString());
                }

                const fullUrl = `${apiUrl}/GetChangeSets?${queryParams.toString()}`;
                console.log('[useGetChangeSets] Calling:', fullUrl);
                await getApiData({
                    endpoint: fullUrl,
                });
            } catch (error) {
                console.error('[useGetChangeSets] Failed to fetch change sets:', error);
            }
        },
        [getApiData, appSettings]
    );

    useEffect(() => {
        if (state.status === 'success' && state.result) {
            console.log('[useGetChangeSets] API Response:', state.result);

            // Extract totalCount from paginated response
            const pagedResult = state.result as ChangeSetPagedResult;
            if (pagedResult && typeof pagedResult.totalCount === 'number') {
                setTotalCount(pagedResult.totalCount);
            }
        } else if (state.status === 'error') {
            console.error('[useGetChangeSets] Error:', state.error);
        }
    }, [state.status, state.result]);

    // Transform the API response to view models
    const getTransformedChangeSets = () => {
        if (state.status === 'success' && state.result) {
            const pagedResult = state.result as ChangeSetPagedResult;

            // Handle paginated response
            if (pagedResult && pagedResult.items && Array.isArray(pagedResult.items)) {
                return mapChangeSetBMs(pagedResult.items);
            }

            // Fallback for non-paginated response (backward compatibility)
            if (Array.isArray(state.result)) {
                return mapChangeSetBMs(state.result as ChangeSetBM[]);
            }
        }
        return [];
    };

    return {
        changeSets: getTransformedChangeSets(),
        totalCount,
        loading: state.isLoading,
        error: state.error,
        getChangeSets,
        status: state.status,
    };
};
